import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LoginManager } from '@shared/core';
import { useIdentify } from '@shared/core';
import type { AuthContextValue, ProvideAuthenticationOptions } from './AuthProvider.context';
import { AuthContext } from './AuthProvider.context';
import type { LogoutEventSessionMutationVariables } from '@graphql/queries/eventSession.graphql.generated';
import { useGetMeQuery, useGetMeLazyQuery } from './AuthProvider.graphql.generated';
import { useLogoutEventSessionMutation, useLoginEventSessionMutation } from '@graphql/queries/eventSession.graphql.generated';
import { useEventNameMatch } from './AuthProvider.utils';
import { useAuthProviderReducer } from './AuthProvider.reducer';
import { identifyUserAction, handleMissingTokenAction, initiateIdentifyUserAction } from './AuthProvider.actions';
import { useMcpPreviewForceSsr } from '@shared/context/mcpPreviewForceSsr';
interface Props {
  loginManager: LoginManager;
}

export const AuthProvider: React.FC<Props> = ({ children, loginManager }) => {
  const eventNameMatch = useEventNameMatch();
  const { identify } = useIdentify();
  // MCP preview: forceSsr keys every auth branch below so server/client agree — see MCP_PREVIEW.md#ssr-anonymous, #hydration-parity.
  const forceSsr = useMcpPreviewForceSsr();
  const [tokenVersion, setTokenVersion] = useState(0);
  const isTokenRefreshRef = useRef(false);

  const [{ hasInitializedOnce, user }, dispatch] = useAuthProviderReducer();

  // ====================================================================
  // Initial auth: eager query (SSR-compatible)
  //
  // Check synchronously if a token exists. If so, fire GetMe eagerly with
  // cache-first so it executes during renderToStringWithData() on the server
  // and reads from Apollo cache on the client during hydration.
  //
  // During SSR, the token is only available if the request cookie contained
  // a valid auth token with a remaining TTL above the minimum threshold
  // (~16.67 minutes, defined by BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS
  // in token.service.ts). Tokens near expiry are rejected by parseAuthCookie
  // to prevent SSR rendering user-specific content that becomes stale before
  // client hydration completes. In that case, SSR renders the unauthenticated
  // view and the client handles auth via the async getTokenAsync() fallback.
  // ====================================================================
  // Capture whether a valid token was available at mount time.
  // Uses the same TTL threshold as parseAuthCookie (~16.67 min) to ensure
  // consistency between SSR and CSR — expired/near-expiry tokens fall back
  // to the async getTokenAsync() path which can refresh them.
  const hadTokenAtMountRef = useRef(loginManager.hasValidTokenForEagerAuth());

  const {
    data: eagerMeData,
    error: eagerMeError,
    loading: eagerMeLoading,
  } = useGetMeQuery({
    variables: {
      // MCP preview: pin refreshMfa false to match the server prewarm's GetMe cache key — see MCP_PREVIEW.md#cache-key-parity.
      refreshMfa: forceSsr ? false : loginManager.authService.shouldRefreshMfaStatus(),
      eventName: eventNameMatch.eventName,
      withActiveEventUser: eventNameMatch.isEventRoute && !!eventNameMatch.eventName,
    },
    batchMode: 'off',
    // cache-first: on client, reads from cache if available (no double-fetch).
    // SSR public-first: GetMe is deferred to client — SSR renders the anonymous-correct
    // public page. After hydration, this query fires and auth state upgrades reactively.
    fetchPolicy: 'cache-first',
    ssr: forceSsr,
    skip: forceSsr ? false : !hadTokenAtMountRef.current,
  });

  // ====================================================================
  // Synchronous state adjustment during render
  //
  // React supports dispatching during render ("adjusting state during rendering").
  // This is necessary because useEffect does NOT fire during SSR, so we can't
  // rely on it to process the eager query result.
  //
  // During renderToStringWithData():
  //   Render pass 1: query loading → no dispatch
  //   Render pass 2: query resolved → dispatch here → React re-renders with updated state
  //   Render pass 3: hasInitializedOnce = true, guard prevents re-dispatch
  //
  // On client hydration with Apollo cache:
  //   Render pass 1: data available from cache → dispatch → re-render with initialized state
  // ====================================================================
  // Track last dispatched data and error separately to detect changes and handle all transitions:
  // - Initial success: data changes from undefined → result
  // - Route navigation: data changes to new event's result
  // - Initial error: error appears while data stays undefined
  // - Error → retry success: data changes while error clears
  const lastDispatchedEagerDataRef = useRef<typeof eagerMeData>(undefined);
  const lastDispatchedEagerErrorRef = useRef<typeof eagerMeError>(undefined);

  // Only dispatch during render for the eager query path (token was available at mount).
  // When no token was available at mount, we do NOT dispatch handleMissingTokenAction here
  // because getTokenAsync() in the useEffect may recover a session via refresh token.
  // Dispatching prematurely would set hasInitializedOnce=true and cause auth-gated components
  // (MustBeSignedIn, guest-site gates) to render the anonymous view before the refresh completes.
  if ((hadTokenAtMountRef.current || forceSsr) && !eagerMeLoading) {
    const hasNewData = eagerMeData !== lastDispatchedEagerDataRef.current;
    const hasNewError = eagerMeError !== lastDispatchedEagerErrorRef.current;

    if (hasNewData || hasNewError) {
      lastDispatchedEagerDataRef.current = eagerMeData;
      lastDispatchedEagerErrorRef.current = eagerMeError;
      // Clear one-shot MFA refresh flag so it's not sent on subsequent mounts.
      // The lazy query path clears this in its onCompleted/onError callbacks,
      // but the eager path bypasses those callbacks.
      // MCP preview: don't clear the one-shot MFA latch (it never refreshed) — see MCP_PREVIEW.md#cache-key-parity.
      if (!forceSsr && loginManager.authService.shouldRefreshMfaStatus()) {
        loginManager.authService.handleRefreshMfaStatusDone();
      }
      if (eagerMeError) {
        dispatch(identifyUserAction({ error: eagerMeError }));
      } else {
        dispatch(identifyUserAction({ data: eagerMeData }));
      }
    }
  }

  // Run identify() when the eager query resolves with user data (client-side only).
  // identify() has side effects (analytics) that should not run during SSR.
  const hasIdentifiedFromEagerRef = useRef(false);
  useEffect(() => {
    if (eagerMeData?.me && !hasIdentifiedFromEagerRef.current) {
      hasIdentifiedFromEagerRef.current = true;
      identify({
        auth0Id: eagerMeData.me.activeAlias?.auth0Id,
        email: eagerMeData.me.email,
        hasMFAEnabled: Boolean(eagerMeData.me.activeAlias?.mfaEnabled),
      });
    }
  }, [eagerMeData, identify]);

  // ====================================================================
  // Token refresh: lazy query (client-only)
  //
  // Uses no-cache because refresh needs fresh data from the server.
  // Triggered by loginManager.accessTokenObservable() when the token changes.
  // ====================================================================
  const [getMeQuery] = useGetMeLazyQuery({
    batchMode: 'off',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      if (loginManager.authService.shouldRefreshMfaStatus()) {
        loginManager.authService.handleRefreshMfaStatusDone();
      }
      if (data.me) {
        identify({
          auth0Id: data.me.activeAlias?.auth0Id,
          email: data.me.email,
          hasMFAEnabled: Boolean(data.me.activeAlias?.mfaEnabled),
        });
      }
      dispatch(identifyUserAction({ data }));
      if (isTokenRefreshRef.current) {
        setTokenVersion(prev => prev + 1);
        isTokenRefreshRef.current = false;
      }
    },
    onError: error => {
      if (loginManager.authService.shouldRefreshMfaStatus()) {
        loginManager.authService.handleRefreshMfaStatusDone();
      }
      dispatch(identifyUserAction({ error }));
      isTokenRefreshRef.current = false;
    },
  });

  const identifyCurrentUser = useCallback(() => {
    const { authService } = loginManager;

    getMeQuery({
      variables: {
        refreshMfa: authService.shouldRefreshMfaStatus(),
        eventName: eventNameMatch.eventName,
        withActiveEventUser: eventNameMatch.isEventRoute && !!eventNameMatch.eventName,
      },
    });
  }, [eventNameMatch.eventName, eventNameMatch.isEventRoute, getMeQuery, loginManager]);

  const processToken = useCallback(
    (token: string | null, isRefresh = false) => {
      if (token) {
        // Track if this is a refresh so we can increment tokenVersion after GetMe completes
        isTokenRefreshRef.current = isRefresh;
        // Only identify if token differs from the current one.
        dispatch(initiateIdentifyUserAction);
        // If token exists, request user identity and event ACLs
        // Executing the lazy query will update the QueryResult value in the result tuple.
        identifyCurrentUser();
      } else {
        // If token does not exist, user is not logged in.
        dispatch(handleMissingTokenAction);
      }
    },
    [dispatch, identifyCurrentUser],
  );

  const providedAuthenticationCodesRef = useRef(new Set<string>());
  // both of these Mutations resolve `null`
  const [loginEventSession] = useLoginEventSessionMutation({ ignoreResults: true });
  const [logoutEventSession] = useLogoutEventSessionMutation({ ignoreResults: true });

  // ====================================================================
  // Client-side token refresh subscription
  //
  // On the client, subscribe to token changes. When the token is refreshed,
  // re-fetch user identity via the lazy query.
  // For clients where the eager query was skipped (no synchronous token),
  // we also handle the initial async token resolution via getTokenAsync().
  // ====================================================================
  // Subscribe once per loginManager. The token subject is a ReplaySubject, so every re-subscription
  // replays the current token and re-dispatches; keying the effect on the callbacks made that happen
  // on renders that changed nothing about auth.
  const processTokenRef = useRef(processToken);
  processTokenRef.current = processToken;

  useEffect(() => {
    // MCP preview: identity is hydrated from the SSR cache, so skip both token-resolution paths — see MCP_PREVIEW.md#ssr-anonymous.
    if (forceSsr) {
      return;
    }

    // If the eager query already initialized auth (SSR or cached), skip the initial getTokenAsync.
    // Only fall back to getTokenAsync when there was no synchronous token (e.g., fresh client load
    // where the token is in localStorage and needs async retrieval).
    if (!hadTokenAtMountRef.current) {
      // No synchronous token at mount — attempt async recovery via refresh token.
      // This is the only path that resolves auth for users whose access token expired
      // between page loads but still have a valid refresh token.
      loginManager.getTokenAsync().then(token => processTokenRef.current(token, false));
    }

    const accessTokenSub = loginManager.accessTokenObservable().subscribe({
      next: token => {
        // Triggered when the observable pushes a new token - typically when it is refreshed.
        processTokenRef.current(token?.id_token ?? null, true);
      },
    });

    return () => {
      accessTokenSub?.unsubscribe();
    };
  }, [loginManager, forceSsr]);

  const authContextValue: AuthContextValue = useMemo(() => {
    // an end-to-end Auth operation require a blend of Auth0 + Gateway trickery
    //   both take an optional { eventId } to login / logout to a specific Event
    //   eg. useful in the case of a Person / Guest / Invitee
    //   vs. an Auth0 User, who would logout from *all* Events
    async function provideAuthentication({ loginVariables, history }: ProvideAuthenticationOptions): Promise<void> {
      const { authService } = loginManager;
      const authResult = authService.deriveAuthorizeResult(history);

      // we should only process a given Authentication code *once*
      //   across the lifespan of this SPA.
      const { code } = authResult;
      const codes = providedAuthenticationCodesRef.current;
      if (codes.has(code)) {
        return;
      }
      codes.add(code);

      let success = false;
      try {
        success = await authService.consumeAuthenticationToken(authResult);
        if (success) {
          // notify the Gateway, *after* Auth handling, but *before* any redirection
          await loginEventSession({ variables: loginVariables });
        }
      } catch (err) {
        console.error('could not provide Authentication');
        console.error(err);
        success = false;
      }

      authService.redirectAfterAuthentication(authResult, history, success);
    }

    function authorize(returnTo = '/', params?: Record<string, string | boolean | number>) {
      return loginManager.authService.authorize(returnTo, params);
    }

    // logout is *much easier*
    // WARNING: you need to reload the page for apollo's me query to respect the new token
    // it will fire onCompleted without a new request with the previously returned value (signed-in user)
    async function provideLogout(variables: LogoutEventSessionMutationVariables, returnUrl?: string): Promise<void> {
      const { authService } = loginManager;
      try {
        // notify the Gateway, *after* the logout
        await logoutEventSession({ variables });
      } catch (err) {
        console.error('could not provide Logout');
        console.error(err);
      } finally {
        // always redirect to auth0 logout route
        authService.signout(returnUrl);
      }
    }

    const isCurrentUserSettled = ['success', 'idle'].includes(user.requestStatus) && hasInitializedOnce;

    return {
      authorize,
      currentUser: user,
      hasInitializedOnce,
      identifyCurrentUser,
      isCurrentUserSettled,
      isLoggedIn: !!user.profile,
      isSuperAdmin: user.profile?.superAdmin ?? false,
      loginManager: loginManager,
      provideAuthentication,
      provideLogout,
      tokenVersion,
      willRefetch: eventNameMatch.shouldRevalidate,
    };
  }, [user, hasInitializedOnce, eventNameMatch.shouldRevalidate, loginManager, identifyCurrentUser, loginEventSession, logoutEventSession, tokenVersion]);

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};
